import React from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useClientSelf } from '@/hooks/use-api';
import { Wallet, Landmark, Calendar, TrendingUp, AlertCircle, History, BellRing } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from "@/lib/utils";
export function ClientDashboard() {
  const { data: client, isLoading } = useClientSelf();
  const handleInPersonAction = (action: string) => {
    toast.info(`Biometric Verification Required`, {
      description: `${action} requires a visit to your local branch for security verification.`
    });
  };
  return (
    <AppLayout container className="bg-accent/20 scrollbar-custom">
      <div className="space-y-8 animate-fade-in">
        <div className="flex flex-col gap-1">
          <h1 className="text-4xl font-black tracking-tight text-foreground uppercase">
            {isLoading ? <Skeleton className="h-10 w-64 bg-muted" /> : `Welcome, ${client?.name || "Member"}`}
          </h1>
          <p className="text-muted-foreground font-bold uppercase tracking-[0.2em] text-[10px]">Institutional Wealth Oversight.</p>
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <Card className="bg-card border-border border-2 rounded-3xl overflow-hidden shadow-soft">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/20">
                  <BellRing className="h-7 w-7" />
                </div>
                <div>
                  <p className="font-black text-foreground uppercase tracking-tight text-lg">Active Savings Cycle</p>
                  <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Secure your daily Susu deposit now.</p>
                </div>
              </div>
              <Button asChild className="bg-primary hover:bg-primary/90 w-full sm:w-auto h-12 px-10 rounded-xl font-black uppercase tracking-tight shadow-lg shadow-primary/30">
                <Link to="/client/payment">Add Funds</Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="bg-primary text-primary-foreground border-none shadow-glow-lg rounded-[2.5rem] overflow-hidden relative p-6 group">
            <div className="absolute top-0 right-0 p-12 opacity-10 group-hover:scale-110 transition-transform duration-700">
               <Wallet className="h-40 w-40" />
            </div>
            <CardHeader><CardTitle className="text-primary-foreground text-[10px] font-black uppercase tracking-[0.3em]">Passbook Balance</CardTitle></CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="text-7xl font-black tracking-tighter">
                {isLoading ? <Skeleton className="h-20 w-64 bg-primary-foreground/20" /> : `GHS ${client?.balance?.toLocaleString() || "0.00"}`}
              </div>
              <div className="flex items-center gap-2 text-primary-foreground/90 text-[10px] font-black uppercase tracking-[0.2em] bg-primary-foreground/10 w-fit px-5 py-2 rounded-full backdrop-blur-md border border-primary-foreground/5">
                <TrendingUp className="h-3.5 w-3.5" />
                {client?.lastPayment ? `Audit: ${new Date(client.lastPayment).toLocaleDateString()}` : "Active Status"}
              </div>
            </CardContent>
          </Card>
<Card className="bg-secondary text-secondary-foreground border-none shadow-glass rounded-[2.5rem] overflow-hidden relative p-6 border-t-8 border-muted">
             <CardHeader><CardTitle className="text-muted-foreground text-[10px] font-black uppercase tracking-[0.3em]">Credit Exposure</CardTitle></CardHeader>
             <CardContent className="space-y-6 pt-6">
               <div className="text-7xl font-black tracking-tighter text-secondary-foreground/95 group-hover:opacity-100 transition-opacity">
                 {isLoading ? <Skeleton className="h-20 w-64 bg-muted" /> : `GHS ${client?.loans?.[0]?.amount?.toLocaleString() || "0.00"}`}
               </div>
               <div className="flex items-center gap-2 text-muted-foreground text-[10px] font-black uppercase tracking-[0.2em] bg-secondary-foreground/5 px-5 py-2 rounded-full border border-secondary-foreground/5">
                 <AlertCircle className="h-3.5 w-3.5" /> 
                 {client?.loans?.[0]?.status || "No Active Loan"}
               </div>
             </CardContent>
           </Card>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: History, label: 'Audit Log', path: '/client/passbook', color: 'text-primary' },
            { icon: Wallet, label: 'Pay Susu', path: '/client/payment', color: 'text-emerald-500' },
            { icon: Landmark, label: 'Apply Loan', action: () => handleInPersonAction('Loan'), color: 'text-muted-foreground' },
            { icon: Calendar, label: 'Visit Branch', action: () => handleInPersonAction('Consult'), color: 'text-muted-foreground' }
          ].map((item, idx) => (
            item.path ? (
              <Link key={idx} to={item.path}>
                <Card className="hover:shadow-glow-lg border-border border-2 p-10 flex flex-col items-center gap-4 transition-all duration-300 rounded-[2rem] bg-card group active:scale-95">
                  <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center group-hover:bg-muted/80 transition-colors shadow-inner">
                    <item.icon className={cn("h-8 w-8", item.color)} />
                  </div>
                  <span className="font-black text-[11px] uppercase tracking-[0.25em] text-foreground">{item.label}</span>
                </Card>
              </Link>
            ) : (
              <button key={idx} onClick={item.action} className="w-full">
                <Card className="hover:shadow-glow-lg border-border border-2 p-10 flex flex-col items-center gap-4 transition-all duration-300 rounded-[2rem] bg-card group active:scale-95">
                  <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center group-hover:bg-muted/80 transition-colors shadow-inner">
                    <item.icon className={cn("h-8 w-8", item.color)} />
                  </div>
                  <span className="font-black text-[11px] uppercase tracking-[0.25em] text-foreground">{item.label}</span>
                </Card>
              </button>
            )
          ))}
        </div>
      </div>
    </AppLayout>
  );
}